import { MealCountRequest, MealCountResult, SaveMealCountRequest, MergeServingLineRequest, SyncCommandResult, CommonResult } from '@/models';
import { injectable } from 'inversify-props';
import axios, { AxiosResponse } from 'axios';
import { mcDate, mcLogger } from '@/utils';
import { IOfflineInfo } from '@/store/counter/state';

export interface ICounterApi {
  /**
   * Pulls any new requested count data from server based on count sync dates.
   * @param request
   */
  syncMealsDown(request: MealCountRequest): Promise<AxiosResponse<CommonResult<MealCountResult>>>;

  /**
   * Syncs device meals to server.
   * @param payload
   */
  syncMealsUp(request: SaveMealCountRequest[]): Promise<AxiosResponse<SyncCommandResult>>;

  /** Gets all info needed for running the app in offline mode: sites, sponsors, site apps for last 45 days. */
  mergeServingLines(request: MergeServingLineRequest[]) : Promise<AxiosResponse<any>>;
  getOfflineInfo(): Promise<AxiosResponse<any>>;

  /** Gets related urls like authUrl and mcAdminUrl */
  getAppSettings(): Promise<AxiosResponse<any>>;

  getUserInfo(): Promise<AxiosResponse<any>>;
}

@injectable()
export class CounterApi implements ICounterApi {
  private readonly console = mcLogger.getConsole(this.constructor.name, mcLogger.colors.CounterApi);

  async syncMealsDown(request: MealCountRequest): Promise<AxiosResponse<CommonResult<MealCountResult>>> {
    const uri = 'api/countingapp/sync/down';
    return axios.post(uri, request)
      .catch(e => e.response);
  }

  async syncMealsUp(payload: SaveMealCountRequest[]): Promise<AxiosResponse<SyncCommandResult>> {
    const timezoneName = mcDate.timeZone();
    const timezoneOffset = mcDate.offset();
    const urlSearchParams = `?timezoneName=${timezoneName}&timezoneOffset=${timezoneOffset}`;
    return axios
      .post(`api/countingapp/sync/up${urlSearchParams}`, payload)
      .catch(e => e.response);
  }

  async mergeServingLines(request: MergeServingLineRequest[]): Promise<AxiosResponse<any>> {
    const timezoneName = mcDate.timeZone();
    const timezoneOffset = mcDate.offset();
    const urlSearchParams = `?timezoneName=${timezoneName}&timezoneOffset=${timezoneOffset}`;
    const uri = `api/countingapp/mergeservinglines${urlSearchParams}`;
    return axios.post(uri, request)
    .catch(e => e.response);
  }

  /** Gets all info needed for running the app in offline mode: sites, sponsors, site apps for last 45 days. */
  async getOfflineInfo(): Promise<AxiosResponse<IOfflineInfo>> {
    return axios
      .get('api/countingapp/offlineinfo')
      .catch(e => e.response);
  }

  /** Gets related urls like authUrl and mcAdminUrl */
  async getAppSettings(): Promise<AxiosResponse<any>> {
    // want to bypass bearer token since this is a public endpoint.
    const client = axios.create();

    return client
      .get('api/countingapp/settings')
      .catch(e => e.response);
  }

  async getUserInfo(): Promise<AxiosResponse<any>> {
    return axios
      .get('api/countingapp/userinfo')
      .catch(e => e.response);
  }
}
